import React, { useEffect } from 'react';
import { Col } from 'react-bootstrap';
import { Field, getIn, useFormikContext } from 'formik';
import { FormikInput, FormikDatePicker } from '../../../components';
import ChoicePreview from './ChoicePreview';
import ImageChoicePreview from './ImageChoicePreview';
import MatrixPreview from './MatrixPreview';
import PreferenceOrderPreview from './PreferenceOrderPreview';
import EmojiScalePreview from './EmojiScalePreview';
import StarScalePreview from './StarScalePreview';
import LikertScalePreview from './LikertScalePreview';
import SemanticDiffPreview from './SemanticDiffPreview';
import PointsScalePreview from './PointsScalePreview';

const QuestionPreview = ({ question, index, sendSurvey, outOfDate }) => {
  const { setFieldValue, setFieldTouched, errors, touched } = useFormikContext();
  const fieldName = `employeeSurvey[employeeQuestionChoicesAttributes][${index}]`;
  const error = getIn(errors, `${fieldName}[questionChoiceId]`);
  const touch = getIn(touched, `${fieldName}[questionId]`);
  const isError = error && touch;

  const { responseFormat, questionType } = question;
  const choices = question.questionChoicesAttributes || [];

  useEffect(() => {
    setFieldValue(`${fieldName}[questionId]`, question.id);
  }, [fieldName, question.id, setFieldValue]);

  const renderChoices = () => {
    if (responseFormat === 'images') {
      return (
        <div className="survey-preview-grid">
          {choices.map(choice => (
            <ImageChoicePreview
              key={`img-${choice.position}`}
              choice={choice}
              index={index}
              sendSurvey={sendSurvey}
              outOfDate={outOfDate}
            />
          ))}
        </div>
      );
    }

    if (questionType === 'structured' && responseFormat === 'matrix') {
      return <MatrixPreview question={question} fieldName={fieldName} sendSurvey={sendSurvey} outOfDate={outOfDate} />;
    }

    if (questionType === 'structured' && responseFormat === 'preference_order') {
      return (
        <PreferenceOrderPreview
          question={question}
          fieldName={fieldName}
          sendSurvey={sendSurvey}
          outOfDate={outOfDate}
        />
      );
    }

    if (questionType === 'evaluation_scale') {
      const sharedProps = { question, fieldName, sendSurvey, outOfDate };
      if (responseFormat === 'emojis') return <EmojiScalePreview {...sharedProps} />;
      if (responseFormat === 'stars') return <StarScalePreview {...sharedProps} />;
      if (responseFormat === 'likert_scale') return <LikertScalePreview {...sharedProps} />;
      if (responseFormat === 'semantic_differential') return <SemanticDiffPreview {...sharedProps} />;
      if (responseFormat === 'points') return <PointsScalePreview {...sharedProps} />;
    }

    if (questionType === 'open') {
      const isText = responseFormat === 'text';
      const maxChars = isText ? 500 : 120;

      const getPlaceholder = () => {
        if (responseFormat === 'email') return 'ejemplo@ejemplo.com';
        if (responseFormat === 'date') return 'dd/mm/aaaa';
        if (responseFormat === 'numeric') return 'Escriba numero(s)';
        return 'Ingrese su respuesta aquí';
      };

      const getType = () => {
        if (responseFormat === 'email') return 'email';
        if (responseFormat === 'date') return 'date';
        if (responseFormat === 'numeric') return 'text';
        return 'text';
      };

      return (
        <Field name={`${fieldName}[answerText]`}>
          {({ field }) => {
            let inputElement = null;

            if (isText) {
              inputElement = (
                <FormikInput
                  as="textarea"
                  rows={3}
                  placeholder={getPlaceholder()}
                  {...field}
                  value={field.value || ''}
                  onChange={e => {
                    if (e.target.value.length <= maxChars) {
                      setFieldTouched(`${fieldName}[questionId]`);
                      setFieldValue(`${fieldName}[answerText]`, e.target.value);
                    }
                  }}
                  disabled={!sendSurvey || outOfDate}
                />
              );
            } else if (responseFormat === 'date') {
              inputElement = (
                <FormikDatePicker
                  name={`${fieldName}[answerText]`}
                  value={field.value}
                  isOutsideRange={() => false}
                  placeholder="dd/mm/aaaa"
                  onChange={val => {
                    setFieldTouched(`${fieldName}[questionId]`);
                    setFieldValue(`${fieldName}[answerText]`, val);
                  }}
                  disabled={!sendSurvey || outOfDate}
                />
              );
            } else {
              inputElement = (
                <FormikInput
                  inputType={getType()}
                  placeholder={getPlaceholder()}
                  maxLength={maxChars}
                  {...field}
                  value={field.value || ''}
                  onChange={e => {
                    let val = e.target.value;
                    if (responseFormat === 'numeric') {
                      val = val.replace(/[^0-9.,-]/g, '');
                    }
                    if (val.length > maxChars && responseFormat !== 'numeric') val = val.slice(0, maxChars);
                    setFieldTouched(`${fieldName}[questionId]`);
                    setFieldValue(`${fieldName}[answerText]`, val);
                  }}
                  disabled={!sendSurvey || outOfDate}
                />
              );
            }

            return (
              <>
                {inputElement}
                <div className="open-answer-counter">
                  {(field.value || '').length}/{maxChars}
                </div>
              </>
            );
          }}
        </Field>
      );
    }

    const isMultiple = responseFormat === 'multiple';
    const isLikert = responseFormat === 'likert_scale';
    const inputType = isMultiple ? 'checkbox' : 'radio';

    return (
      <Field name={`${fieldName}[questionChoiceId]`}>
        {({ field }) => (
          <div className={isLikert ? 'likert' : ''}>
            {choices.map(choice => (
              <ChoicePreview
                key={`choice-${choice.id || choice.position}`}
                choice={choice}
                field={field}
                checkClass={isLikert ? 'likert-choice' : 'question-choice'}
                index={index}
                sendSurvey={sendSurvey}
                outOfDate={outOfDate}
                inputType={inputType}
              />
            ))}
          </div>
        )}
      </Field>
    );
  };

  const capitalize = str => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  return (
    <Col md={11} className="bg-light round-10 ml-3 mr-n3 mb-3">
      <div className={`p-3 ${isError ? 'card-error' : ''}`}>
        <h5 className="mb-1 survey-preview-question-title">
          {`${index + 1}. ${capitalize(question.label)}`}
          {question.required && <span className="text-danger ml-1">*</span>}
        </h5>
        {question.description && <p className="text-muted mb-1 survey-preview-question-desc">{capitalize(question.description)}</p>}
        {renderChoices()}
        {isError && <small className="text-danger mb-2 d-block">{error}</small>}
      </div>
    </Col>
  );
};

export default QuestionPreview;
